/**
 * #334 — the one poll loop every read-only relay status hook should use.
 *
 * The relay status hooks (vanilla day, vanilla report — and, before their lanes
 * were pruned, rita panel and edge fleet) each hand-rolled the same block:
 * fetch once on mount, `setInterval` at the same cadence
 * forever, an `alive` ref so a late response doesn't land after unmount, and a
 * cleanup that clears the timer. The bug in every copy was the same one — the
 * interval kept running at trading cadence in a tab nobody was looking at.
 *
 * This hook owns that block, so a caller writes the read and nothing else. It
 * runs the read immediately, then on `startVisibilityAwareInterval`: full
 * cadence while visible, 1/HIDDEN_DIVISOR while hidden, one immediate read the
 * moment the tab comes back so a returning operator never reads throttled data.
 *
 * ─── Why the signal, and why it isn't wired to fetch ─────────────────────────
 *
 * Each arm of the loop owns an AbortController, and `poll` receives its signal.
 * `signal.aborted` means "whatever you're holding is no longer wanted" — the
 * hook unmounted, polling was disabled, or the loop re-armed under a new scope
 * and a newer read is already out. A read must check it before committing
 * state, exactly where the hand-rolled copies checked their `alive` ref.
 *
 * It is deliberately NOT passed to `apiFetch`. Aborting the request itself
 * would save only the tail of a response that is already on the wire, and it
 * would surface `AbortError` in each caller's catch block — turning a normal
 * unmount into a red error state unless every caller re-guarded. Callers that
 * later want true cancellation can forward it themselves; the guard semantics
 * do not change if they do.
 *
 * ─── Why `poll` belongs in the dep array ─────────────────────────────────────
 *
 * `poll` must be a `useCallback`. It is a dependency rather than a latched ref
 * on purpose: when its identity changes because the read's scope changed (an
 * admin switching orgs), the loop re-arms, aborts the old
 * scope's in-flight read, and fires an immediate read for the new one. A
 * latched ref would leave the panel showing the previous org until the next
 * tick.
 */
import { useCallback, useEffect, useRef } from 'react';

import { startVisibilityAwareInterval } from '@/lib/visibilityThrottle';

export interface UseVisibilityAwarePoll {
  /**
   * Run the read now, off-cadence, under the live arm's signal. For callers to
   * expose as their own `refresh` and to call after a mutation — a write's
   * follow-up read is never throttled and never waits for the next tick.
   */
  refresh: () => Promise<void>;
}

/**
 * Poll `poll` every `pollMs` while visible and every `pollMs × HIDDEN_DIVISOR`
 * while hidden, with an immediate read on mount and on tab wake.
 *
 * `enabled: false` (default true) polls nothing, aborts any read in flight, and
 * makes `refresh()` a no-op — for callers gated on permission or on a closed
 * panel, where "the loop is off" has to mean no read at all.
 */
export function useVisibilityAwarePoll(
  poll: (signal: AbortSignal) => void | Promise<void>,
  pollMs: number,
  opts?: { enabled?: boolean },
): UseVisibilityAwarePoll {
  const enabled = opts?.enabled ?? true;
  // Null before the first arm mounts and aborted after the last one tears down,
  // so an off-cadence `refresh()` outside the loop's lifetime is a no-op rather
  // than a read that commits state to an unmounted caller.
  const armRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const arm = new AbortController();
    armRef.current = arm;
    // A disabled loop owns no live read, so its arm starts aborted: `enabled`
    // is a permission gate at the call sites that pass it, and an off-cadence
    // `refresh()` must not be the hole that fires the authed read anyway.
    if (!enabled) {
      arm.abort();
      return;
    }

    const tick = () => void poll(arm.signal);
    tick();
    const stop = startVisibilityAwareInterval(tick, pollMs);
    return () => {
      arm.abort();
      stop();
    };
  }, [enabled, poll, pollMs]);

  const refresh = useCallback(async () => {
    const arm = armRef.current;
    if (!arm || arm.signal.aborted) return;
    await poll(arm.signal);
  }, [poll]);

  return { refresh };
}
